/**
 * Embed an XMP packet into a JPEG by inserting an `APP1` segment (XMP
 * namespace header per XMP Specification Part 3) immediately after any
 * leading APP0/APP1 segments, before image data. Like the PNG counterpart
 * (`png-metadata.ts`) this is pure byte manipulation — no re-encode, no
 * native deps — so it runs identically in the browser and in Node. Needed
 * because canvas re-encoding to JPEG (the HTML5-bundle size ladder) produces
 * a file with no metadata, which would silently drop the AI disclosure.
 *
 * JPEG structure: SOI marker (FFD8), then a sequence of segments, each
 * `marker(FF xx) + length(2 BE, includes itself) + data`, until image data.
 */

const SOI = [0xff, 0xd8];
const XMP_NAMESPACE_HEADER = "http://ns.adobe.com/xap/1.0/\0";
const APP1_MARKER = 0xe1;
const APP0_MARKER = 0xe0;
// Segment length is a 2-byte field that includes its own 2 bytes.
const MAX_SEGMENT_DATA_LENGTH = 0xffff - 2;

export function injectXmpIntoJpeg(jpeg: Uint8Array, xmp: string): Uint8Array {
  assertJpegSignature(jpeg);
  const segment = buildXmpApp1Segment(xmp);
  const insertAt = findInsertionOffset(jpeg);

  const out = new Uint8Array(jpeg.length + segment.length);
  out.set(jpeg.subarray(0, insertAt), 0);
  out.set(segment, insertAt);
  out.set(jpeg.subarray(insertAt), insertAt + segment.length);
  return out;
}

function assertJpegSignature(jpeg: Uint8Array): void {
  if (jpeg.length < 2 || jpeg[0] !== SOI[0] || jpeg[1] !== SOI[1]) {
    throw new Error("injectXmpIntoJpeg: input is not a JPEG (bad signature)");
  }
}

/**
 * XMP convention places the APP1 packet after the JFIF APP0 (and any Exif
 * APP1) so those remain the leading segments readers expect; skipping past
 * any run of APP0/APP1 achieves that regardless of which are present.
 */
function findInsertionOffset(jpeg: Uint8Array): number {
  const view = new DataView(jpeg.buffer, jpeg.byteOffset, jpeg.byteLength);
  let offset = SOI.length;
  while (offset + 4 <= jpeg.length && view.getUint8(offset) === 0xff) {
    const marker = view.getUint8(offset + 1);
    if (marker !== APP0_MARKER && marker !== APP1_MARKER) break;
    offset += 2 + view.getUint16(offset + 2);
  }
  return offset;
}

function buildXmpApp1Segment(xmp: string): Uint8Array {
  const encoder = new TextEncoder();
  const headerBytes = encoder.encode(XMP_NAMESPACE_HEADER);
  const xmpBytes = encoder.encode(xmp);

  const dataLength = headerBytes.length + xmpBytes.length;
  if (dataLength > MAX_SEGMENT_DATA_LENGTH) {
    throw new Error("injectXmpIntoJpeg: XMP packet exceeds APP1 segment size");
  }

  const segment = new Uint8Array(4 + dataLength);
  const view = new DataView(segment.buffer);
  view.setUint8(0, 0xff);
  view.setUint8(1, APP1_MARKER);
  view.setUint16(2, dataLength + 2);
  segment.set(headerBytes, 4);
  segment.set(xmpBytes, 4 + headerBytes.length);
  return segment;
}
